import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { subscriptionAPI, usageAPI } from '../lib/api';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import SubscriptionSidebarLayout from '../components/SubscriptionSidebarLayout';

/** Match addon.applicablePlanTypes (comma-separated) to a plan type without substring bugs. */
const addonAppliesToPlanType = (addon, planType) => {
    if (!planType) return false;
    if (!addon?.applicablePlanTypes) return true;
    return addon.applicablePlanTypes.split(',').map((s) => s.trim()).includes(planType);
};

const MySubscription = () => {
    const navigate = useNavigate();
    const { tenantId } = useParams();
    const [subscription, setSubscription] = useState(null);
    const [addons, setAddons] = useState([]);
    const [usage, setUsage] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [plans, setPlans] = useState([]);
    const [availableAddons, setAvailableAddons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cancelling, setCancelling] = useState(false);
    const [subscribing, setSubscribing] = useState(false);
    const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });
    const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const plansSectionRef = useRef(null);

    const scrollToPlans = () => plansSectionRef.current?.scrollIntoView({ behavior: 'smooth' });

    const getApplicablePlanType = () => {
        switch (user.role) {
            case 'STARTUP':
            case 'ENTREPRENEUR':
                return 'STARTUP';
            case 'INVESTOR':
                return 'INVESTOR';
            case 'INVESTOR_NETWORK':
            case 'MANAGEMENT':
                return 'INVESTOR_NETWORK';
            default:
                return null;
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [
                subscriptionData,
                addonsData,
                usageData,
                alertsData,
                plansData,
                addonsCatalogData
            ] = await Promise.all([
                subscriptionAPI.getMySubscription().catch(() => null),
                subscriptionAPI.getMyAddons().catch(() => []),
                usageAPI.getUsageSummary().catch(() => null),
                usageAPI.getUsageAlerts().catch(() => []),
                subscriptionAPI.getPlans().catch(() => []),
                subscriptionAPI.getAddons().catch(() => [])
            ]);
            setSubscription(subscriptionData);
            setAddons(addonsData);
            setUsage(usageData);
            setAlerts(alertsData);

            const applicablePlanType = getApplicablePlanType();
            let filteredPlans = applicablePlanType
                ? (plansData || []).filter(plan => plan.planType === applicablePlanType)
                : (plansData || []);
            if (user.role === 'INVESTOR') {
                filteredPlans = filteredPlans.filter(plan => plan.basePrice === 0);
            }
            filteredPlans = filteredPlans.filter((plan) => {
                if (plan.planType === 'STARTUP') {
                    const name = (plan.planName || '').toLowerCase();
                    return !name.includes('startup pro') && !name.includes('startup enterprise');
                }
                if (plan.planType === 'INVESTOR_NETWORK') {
                    const name = (plan.planName || '').toLowerCase();
                    return !name.includes('network pro') && !name.includes('network enterprise');
                }
                return true;
            });
            setPlans(filteredPlans);

            const filteredAddonsCatalog = !applicablePlanType
                ? []
                : (addonsCatalogData || []).filter((addon) => addonAppliesToPlanType(addon, applicablePlanType));
            setAvailableAddons(filteredAddonsCatalog);
        } catch (error) {
            console.error('Failed to load data:', error);
            setAlert({ isOpen: true, title: 'Error', message: error.message, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = (immediate) => {
        setConfirm({
            isOpen: true,
            title: immediate ? 'Cancel Subscription Immediately' : 'Cancel Subscription',
            message: immediate
                ? 'Are you sure you want to cancel your subscription immediately? You will lose access to all features right away.'
                : 'Are you sure you want to cancel your subscription? It will remain active until the end of your billing period.',
            type: 'danger',
            onConfirm: () => performCancel(immediate)
        });
    };

    const performCancel = async (immediate) => {
        setCancelling(true);
        try {
            await subscriptionAPI.cancelSubscription(immediate);
            setAlert({
                isOpen: true,
                title: 'Success',
                message: immediate ? 'Subscription cancelled immediately' : 'Subscription will be cancelled at the end of the billing period',
                type: 'success'
            });
            loadData();
        } catch (error) {
            console.error('Cancellation failed:', error);
            setAlert({ isOpen: true, title: 'Error', message: error.message || 'Cancellation failed', type: 'error' });
        } finally {
            setCancelling(false);
        }
    };

    const getPlanFeatures = (plan) => {
        const features = [];
        if (plan.storageLimitGb > 0) features.push(`${plan.storageLimitGb} GB Storage`);
        if (plan.investorLinkLimit > 0) features.push(`Up to ${plan.investorLinkLimit} Investor Links`);
        if (plan.planType === 'STARTUP') {
            features.push('Upload & share documents', 'Investor data room access', 'Investment tracking');
            if (plan.storageLimitGb >= 25) features.push('Priority support');
            if (plan.storageLimitGb >= 100) features.push('Dedicated account manager', 'Custom branding');
        } else if (plan.planType === 'INVESTOR') {
            features.push('View shared documents', 'Portfolio tracking', 'Investment analytics');
            if (plan.basePrice > 0) features.push('Priority deal access', 'Advanced reporting');
        } else if (plan.planType === 'INVESTOR_NETWORK') {
            features.push('Manage investor users', 'Network investment tracking', 'Commitment management');
            if (plan.investorLinkLimit >= 100) features.push('Priority support');
            if (plan.investorLinkLimit >= 500) features.push('Dedicated account manager', 'API access');
        }
        return features;
    };

    const getAddonLinesForPlan = (plan) => {
        if (!plan?.planType) return [];
        return availableAddons
            .filter((addon) => addonAppliesToPlanType(addon, plan.planType))
            .map((addon) => {
                if (addon.addonCategory === 'STORAGE') {
                    return `Storage add-on: +${addon.unitValue} GB @ ₹${addon.unitPrice}`;
                }
                if (addon.addonCategory === 'INVESTOR_LINK') {
                    return `Investor link add-on: +${addon.unitValue} investors @ ₹${addon.unitPrice}`;
                }
                return `${addon.addonName}: ₹${addon.unitPrice}`;
            });
    };

    const getRecommendedPlan = () => {
        if (!user.role) return null;
        if (user.role === 'STARTUP' || user.role === 'ENTREPRENEUR') return plans.find(p => p.planCode === 'STARTUP_BASE');
        if (user.role === 'INVESTOR') return plans.find(p => p.planCode === 'INVESTOR_FREE');
        if (user.role === 'INVESTOR_NETWORK' || user.role === 'MANAGEMENT') return plans.find(p => p.planCode === 'NETWORK_BASE');
        return null;
    };

    const isPlanActive = (planCode) =>
        subscription?.plan?.planCode === planCode && subscription?.status === 'ACTIVE';

    const hasActiveSubscription = () => subscription?.status === 'ACTIVE';

    const isUpgrade = (plan) => {
        if (!hasActiveSubscription()) return false;
        return (plan.basePrice || 0) > (subscription?.plan?.basePrice || 0);
    };

    const handleSubscribe = (plan) => {
        const rolePlanMap = { STARTUP: 'STARTUP', ENTREPRENEUR: 'STARTUP', INVESTOR: 'INVESTOR', INVESTOR_NETWORK: 'INVESTOR_NETWORK', MANAGEMENT: 'INVESTOR_NETWORK' };
        const expectedPlanType = rolePlanMap[user.role];
        if (plan.planType !== expectedPlanType) {
            setAlert({ isOpen: true, title: 'Invalid Plan', message: `This plan is not available for your account type.`, type: 'error' });
            return;
        }
        const hasExisting = hasActiveSubscription();
        const currentPlanName = subscription?.plan?.planName || '';
        const isAnnual = plan.billingCycle === 'ANNUAL';
        const priceText = plan.basePrice === 0 ? 'Free' : (isAnnual ? `₹${plan.basePrice}/year` : `₹${plan.basePrice}/month`);
        let title, message;
        if (hasExisting) {
            const upgradeOrSwitch = isUpgrade(plan) ? 'Upgrade' : 'Switch';
            title = `${upgradeOrSwitch} to ${plan.planName}`;
            message = `You are currently on ${currentPlanName}. ${upgradeOrSwitch}ing to ${plan.planName} (${priceText}) will cancel your current subscription and start the new plan immediately. Continue?`;
        } else {
            title = 'Subscribe to Plan';
            message = plan.basePrice === 0 ? `Start your free ${plan.planName} subscription?` : `Subscribe to ${plan.planName} for ${priceText}?`;
        }
        setConfirm({
            isOpen: true,
            title,
            message,
            type: hasExisting ? 'warning' : 'info',
            onConfirm: () => performSubscribe(plan.planCode, hasExisting)
        });
    };

    const performSubscribe = async (planCode, cancelExisting = false) => {
        setSubscribing(true);
        try {
            if (cancelExisting) await subscriptionAPI.cancelSubscription(true);
            await subscriptionAPI.subscribe(planCode);
            setAlert({ isOpen: true, title: 'Success', message: cancelExisting ? 'Subscription upgraded successfully!' : 'Subscription created successfully!', type: 'success' });
            loadData();
        } catch (error) {
            console.error('Subscription failed:', error);
            setAlert({ isOpen: true, title: 'Error', message: error.message || 'Subscription failed', type: 'error' });
        } finally {
            setSubscribing(false);
        }
    };

    const getUsageBarClass = (percentage) => {
        if (percentage >= 100) return 'bg-red-500';
        if (percentage >= 90) return 'bg-orange-500';
        if (percentage >= 80) return 'bg-amber-500';
        return 'bg-emerald-500';
    };

    const subscriptionStatusClass = (status) => {
        if (status === 'ACTIVE') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
        if (status === 'CANCELLED') return 'bg-slate-100 text-slate-700 border-slate-200';
        return 'bg-red-100 text-red-800 border-red-200';
    };

    const formatPlanPrice = (plan) => {
        if (!plan || plan.basePrice === 0) return 'Free';
        return plan.billingCycle === 'ANNUAL' ? `₹${plan.basePrice}/year` : `₹${plan.basePrice}/mo`;
    };

    const alertBannerClass = (level) => {
        if (level === 'EXCEEDED') return 'bg-red-50 border-red-200 text-red-900';
        if (level === 'CRITICAL') return 'bg-orange-50 border-orange-200 text-orange-900';
        return 'bg-amber-50 border-amber-200 text-amber-900';
    };

    const alertIconClass = (level) => {
        if (level === 'EXCEEDED') return 'text-red-600 bg-red-100';
        if (level === 'CRITICAL') return 'text-orange-600 bg-orange-100';
        return 'text-amber-600 bg-amber-100';
    };

    if (loading) {
        return (
            <SubscriptionSidebarLayout activeItem="subscription">
                <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
                    <div className="animate-spin rounded-full h-11 w-11 border-2 border-emerald-200 border-t-emerald-600" />
                    <p className="text-sm text-slate-500 font-medium">Loading subscription…</p>
                </div>
            </SubscriptionSidebarLayout>
        );
    }

    const hasPlan = subscription?.id;
    const storagePct = usage?.storageUsage?.percentage ?? 0;
    const linksPct = usage?.investorLinkUsage?.percentage ?? 0;

    return (
        <SubscriptionSidebarLayout activeItem="subscription">
            {/* Page hero */}
            <div className="relative overflow-hidden rounded-2xl border border-emerald-200/60 bg-gradient-to-br from-emerald-600 via-teal-600 to-teal-700 text-white shadow-lg shadow-emerald-900/10 mb-8">
                <div className="absolute top-0 right-0 w-72 h-72 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
                <div className="relative px-6 py-8 sm:px-8 sm:py-10">
                    <div className="flex flex-wrap items-start justify-between gap-6">
                        <div>
                            <p className="text-emerald-100 text-sm font-semibold uppercase tracking-wider mb-1">Account billing</p>
                            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">My Subscription</h1>
                            <p className="text-emerald-50/90 mt-2 max-w-xl text-sm sm:text-base">
                                {hasPlan
                                    ? `You're on ${subscription.plan?.planName || 'your plan'}. Review usage, add-ons, and change plan anytime.`
                                    : 'Choose a plan to unlock storage, investor links, and platform features.'}
                            </p>
                        </div>
                        {hasPlan && (
                            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold border ${subscriptionStatusClass(subscription.status)} bg-white/95 shadow-sm`}>
                                {subscription.status}
                            </span>
                        )}
                    </div>
                    {hasPlan && (
                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-3">
                                <p className="text-emerald-100 text-xs font-medium uppercase tracking-wide">Current plan</p>
                                <p className="text-lg font-bold mt-0.5 truncate">{subscription.plan?.planName}</p>
                                <p className="text-emerald-100/80 text-sm">{formatPlanPrice(subscription.plan)}</p>
                            </div>
                            {usage?.storageUsage && (
                                <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-3">
                                    <p className="text-emerald-100 text-xs font-medium uppercase tracking-wide">Storage</p>
                                    <p className="text-lg font-bold mt-0.5">{storagePct.toFixed(0)}% used</p>
                                    <p className="text-emerald-100/80 text-sm truncate">
                                        {usage.storageUsage.displayUsed} / {usage.storageUsage.displayLimit}
                                    </p>
                                </div>
                            )}
                            {usage?.investorLinkUsage && (
                                <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-3">
                                    <p className="text-emerald-100 text-xs font-medium uppercase tracking-wide">Investor links</p>
                                    <p className="text-lg font-bold mt-0.5">{linksPct.toFixed(0)}% used</p>
                                    <p className="text-emerald-100/80 text-sm">
                                        {usage.investorLinkUsage.used} / {usage.investorLinkUsage.limit}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Usage alerts */}
            {alerts.length > 0 && (
                <div className="mb-8 space-y-3">
                    {alerts.map((alertItem) => (
                        <div
                            key={alertItem.id}
                            className={`flex flex-wrap items-center justify-between gap-4 p-4 rounded-xl border ${alertBannerClass(alertItem.alertLevel)}`}
                        >
                            <div className="flex items-start gap-3 min-w-0">
                                <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${alertIconClass(alertItem.alertLevel)}`}>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <div className="min-w-0">
                                    <h4 className="font-semibold text-sm">
                                        {alertItem.usageType === 'STORAGE' ? 'Storage' : 'Investor links'} — {alertItem.alertLevel}
                                    </h4>
                                    <p className="text-sm opacity-90 mt-0.5">
                                        {alertItem.message} · {alertItem.displayUsed} / {alertItem.displayLimit}
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={scrollToPlans}
                                className="shrink-0 px-4 py-2 text-sm font-semibold rounded-lg bg-white border border-current/20 shadow-sm hover:bg-white/80 transition-colors"
                            >
                                View plans
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-10">
                {/* Current plan + add-ons */}
                <div className="xl:col-span-2 space-y-6">
                    {hasPlan ? (
                        <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm">
                            <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white flex flex-wrap items-center justify-between gap-3">
                                <h2 className="text-lg font-semibold text-gray-900">Current plan</h2>
                                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${subscriptionStatusClass(subscription.status)}`}>
                                    {subscription.status}
                                </span>
                            </div>
                            <div className="p-6">
                                <div className="flex flex-wrap items-baseline gap-3 mb-6">
                                    <h3 className="text-2xl font-bold text-gray-900">{subscription.plan?.planName}</h3>
                                    <span className="text-xl font-semibold text-emerald-600">{formatPlanPrice(subscription.plan)}</span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {[
                                        { label: 'Storage included', value: `${subscription.plan?.storageLimitGb ?? '—'} GB` },
                                        ...(subscription.plan?.investorLinkLimit > 0
                                            ? [{ label: 'Investor links', value: String(subscription.plan.investorLinkLimit) }]
                                            : []),
                                        { label: 'Started', value: subscription.startDate ? new Date(subscription.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—' },
                                        { label: 'Renews', value: subscription.endDate ? new Date(subscription.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A' },
                                        { label: 'Auto-renew', value: subscription.autoRenew ? 'On' : 'Off', highlight: subscription.autoRenew ? 'text-emerald-600' : 'text-amber-600' }
                                    ].map((row) => (
                                        <div key={row.label} className="rounded-xl bg-slate-50/80 border border-slate-100 px-4 py-3">
                                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{row.label}</p>
                                            <p className={`text-sm font-semibold text-gray-900 mt-1 ${row.highlight || ''}`}>{row.value}</p>
                                        </div>
                                    ))}
                                </div>
                                {subscription.status === 'ACTIVE' ? (
                                    <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-6 border-t border-gray-100">
                                        <button
                                            type="button"
                                            onClick={() => handleCancel(false)}
                                            disabled={cancelling}
                                            className="flex-1 py-2.5 px-4 text-sm font-semibold rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors"
                                        >
                                            Cancel at period end
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleCancel(true)}
                                            disabled={cancelling}
                                            className="flex-1 py-2.5 px-4 text-sm font-semibold rounded-xl border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50 transition-colors"
                                        >
                                            Cancel immediately
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={scrollToPlans}
                                        className="mt-6 w-full py-2.5 px-4 text-sm font-semibold rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:opacity-95 transition-opacity"
                                    >
                                        Choose a plan
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-2xl border-2 border-dashed border-emerald-200 bg-emerald-50/40 p-8 sm:p-10 text-center">
                            <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white mb-4 shadow-lg shadow-emerald-500/25">
                                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                </svg>
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">No active subscription</h2>
                            <p className="text-gray-600 mb-6 max-w-md mx-auto text-sm">Pick a plan below to get storage, investor links, and full platform access.</p>
                            <button
                                type="button"
                                onClick={scrollToPlans}
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 shadow-md hover:opacity-95"
                            >
                                Browse plans
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                            </button>
                        </div>
                    )}

                    {addons.length > 0 && (
                        <div className="rounded-2xl border border-gray-200/80 bg-white shadow-sm overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-100">
                                <h2 className="text-lg font-semibold text-gray-900">Purchased add-ons</h2>
                            </div>
                            <div className="divide-y divide-gray-100">
                                {addons.map((addon) => (
                                    <div key={addon.id} className="flex flex-wrap items-center justify-between gap-4 px-6 py-4 hover:bg-slate-50/50 transition-colors">
                                        <div>
                                            <h3 className="font-semibold text-gray-900">{addon.addon?.addonName}</h3>
                                            <p className="text-sm text-gray-500 mt-0.5">
                                                Qty {addon.quantity} · {new Date(addon.purchaseDate).toLocaleDateString('en-IN')}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-semibold text-gray-900">₹{Number(addon.totalAmount || 0).toLocaleString('en-IN')}</p>
                                            <span className={`inline-flex mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${addon.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                                                {addon.status}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Usage sidebar */}
                <div className="space-y-6">
                    {usage?.storageUsage && (
                        <div className="rounded-2xl border border-gray-200/80 bg-white shadow-sm p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" /></svg>
                                </div>
                                <h3 className="font-semibold text-gray-900">Storage</h3>
                            </div>
                            <div className="flex justify-between text-sm mb-2">
                                <span className="text-gray-500">Used</span>
                                <span className="font-semibold text-gray-900">{usage.storageUsage.displayUsed} / {usage.storageUsage.displayLimit}</span>
                            </div>
                            <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full transition-all ${getUsageBarClass(storagePct)}`} style={{ width: `${Math.min(storagePct, 100)}%` }} />
                            </div>
                            <p className="text-right text-xs text-slate-500 mt-2">{storagePct.toFixed(1)}%</p>
                        </div>
                    )}
                    {usage?.investorLinkUsage && (
                        <div className="rounded-2xl border border-gray-200/80 bg-white shadow-sm p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-xl bg-teal-100 text-teal-700 flex items-center justify-center">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                </div>
                                <h3 className="font-semibold text-gray-900">Investor links</h3>
                            </div>
                            <div className="flex justify-between text-sm mb-2">
                                <span className="text-gray-500">Used</span>
                                <span className="font-semibold text-gray-900">{usage.investorLinkUsage.used} / {usage.investorLinkUsage.limit}</span>
                            </div>
                            <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full transition-all ${getUsageBarClass(linksPct)}`} style={{ width: `${Math.min(linksPct, 100)}%` }} />
                            </div>
                            <p className="text-right text-xs text-slate-500 mt-2">{linksPct.toFixed(1)}%</p>
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={() => navigate(`/${tenantId}/billing`)}
                        className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-slate-50 shadow-sm transition-colors"
                    >
                        <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                        View billing & invoices
                    </button>
                </div>
            </div>

            {/* Plans */}
            <div ref={plansSectionRef} className="scroll-mt-8">
                {user.role === 'INVESTOR' && (
                    <div className="mb-8 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-6 sm:p-8">
                        <h2 className="text-lg font-bold text-gray-900 mb-2">Add documents or startups?</h2>
                        <p className="text-gray-600 text-sm mb-4 max-w-2xl">
                            Pay a one-time fee of <strong className="text-gray-900">₹1,000</strong> to upload documents or be linked to startups.
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate(`/${tenantId}/investor-activity-fee`)}
                            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 shadow-sm transition-colors"
                        >
                            Pay ₹1,000 (one-time)
                        </button>
                    </div>
                )}

                <div className="mb-6">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Available plans</h2>
                    <p className="text-gray-600 mt-1 text-sm">Compare features and switch or upgrade anytime.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {plans.map((plan) => {
                        const isRecommended = getRecommendedPlan()?.planCode === plan.planCode;
                        const isActive = isPlanActive(plan.planCode);
                        const features = [...getPlanFeatures(plan), ...getAddonLinesForPlan(plan)];
                        const upgrade = isUpgrade(plan);

                        return (
                            <div
                                key={plan.id}
                                className={`relative flex flex-col rounded-2xl border bg-white overflow-hidden transition-shadow hover:shadow-md ${
                                    isActive
                                        ? 'border-emerald-400 ring-2 ring-emerald-500/30 shadow-md'
                                        : isRecommended
                                            ? 'border-teal-300 shadow-md'
                                            : 'border-gray-200/80 shadow-sm'
                                }`}
                            >
                                {(isRecommended || isActive) && (
                                    <div className={`absolute top-4 right-4 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                                        isActive ? 'bg-emerald-600 text-white' : 'bg-teal-600 text-white'
                                    }`}>
                                        {isActive ? 'Current' : 'Recommended'}
                                    </div>
                                )}
                                <div className="p-6 flex flex-col flex-1">
                                    <h3 className="text-lg font-bold text-gray-900 pr-20">{plan.planName}</h3>
                                    <div className="mt-3 mb-5">
                                        <span className="text-3xl font-bold text-gray-900">
                                            {plan.basePrice === 0 ? 'Free' : `₹${plan.basePrice}`}
                                        </span>
                                        {plan.basePrice > 0 && (
                                            <span className="text-gray-500 text-sm ml-1">
                                                /{plan.billingCycle === 'ANNUAL' ? 'year' : 'month'}
                                            </span>
                                        )}
                                    </div>
                                    <ul className="space-y-2.5 mb-6 flex-1">
                                        {features.map((feature, index) => (
                                            <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                                                <svg className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        type="button"
                                        onClick={() => handleSubscribe(plan)}
                                        disabled={isActive || subscribing}
                                        className={`w-full py-2.5 px-4 rounded-xl text-sm font-semibold transition-all ${
                                            isActive
                                                ? 'bg-emerald-50 text-emerald-800 border border-emerald-200 cursor-not-allowed'
                                                : hasActiveSubscription()
                                                    ? upgrade
                                                        ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:opacity-95 shadow-sm'
                                                        : 'bg-slate-800 text-white hover:bg-slate-900'
                                                    : isRecommended
                                                        ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:opacity-95 shadow-sm'
                                                        : 'border border-gray-300 text-gray-800 hover:bg-gray-50'
                                        }`}
                                    >
                                        {isActive
                                            ? 'Current plan'
                                            : hasActiveSubscription()
                                                ? upgrade
                                                    ? 'Upgrade'
                                                    : 'Switch plan'
                                                : 'Get started'}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {availableAddons.length > 0 && (
                    <div className="mt-12">
                        <h2 className="text-xl font-bold text-gray-900 mb-2">Available add-ons</h2>
                        <p className="text-sm text-gray-600 mb-6">Extend storage or investor link limits without changing your plan.</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {availableAddons.map((addon) => (
                                <div key={addon.id} className="rounded-2xl border border-gray-200/80 bg-white p-5 shadow-sm hover:border-emerald-200 hover:shadow-md transition-all">
                                    <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3">
                                        {addon.addonCategory === 'STORAGE' ? (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7" /></svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                                        )}
                                    </div>
                                    <h3 className="font-semibold text-gray-900">{addon.addonName}</h3>
                                    <p className="text-sm text-gray-500 mt-1 mb-4">
                                        {addon.addonCategory === 'STORAGE'
                                            ? `+${addon.unitValue} GB storage`
                                            : `+${addon.unitValue} investor links`}
                                    </p>
                                    <p className="text-2xl font-bold text-gray-900 mb-4">₹{addon.unitPrice}</p>
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/${tenantId}/purchase-addon`, { state: { addon } })}
                                        className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:opacity-95 transition-opacity"
                                    >
                                        Purchase
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Alert Dialog */}
            <AlertDialog
                isOpen={alert.isOpen}
                onClose={() => setAlert({ ...alert, isOpen: false })}
                title={alert.title}
                message={alert.message}
                type={alert.type}
            />

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={confirm.isOpen}
                onClose={() => setConfirm({ ...confirm, isOpen: false })}
                onConfirm={confirm.onConfirm || (() => {})}
                title={confirm.title}
                message={confirm.message}
                type={confirm.type}
            />
        </SubscriptionSidebarLayout>
    );
};

export default MySubscription;
